<template>
  <div class="hong-kong-minute-chart">
    <div class="index-tabs">
      <button v-for="(item, index) in indexTabs" :key="item.code" :class="{ active: selectedIndex === index }" type="button" @click="select(index)">{{ item.name }}</button>
    </div>
    <div ref="chart" class="chart"></div>
    <div v-if="error" class="empty">{{ error }}</div>
  </div>
</template>

<script>
import { pollMarket, marketRequest, MARKET_REFRESH_MS, publicBody } from '@/utils/marketRefresh'
import HQChart from 'hqchart'
import { applyChartPricePrecision } from '@/utils/chartPricePrecision'
import DefaultData from '@/utils/DefaultData'
import { applyChartTheme } from '@/utils/chartTheme'

export default {
  name: 'HongKongMinuteChart',
  props: { indexes: { type: Array, default: () => [] }, active: { type: Boolean, default: true } },
  data() {
    return {
      selectedIndex: 0, chart: null, error: '', option: DefaultData.GetMinuteOption(),
      indexTabs: [
        { code: 'hkHSI', name: '恒生指数' },
        { code: 'hkHSCEI', name: '国企指数' },
        { code: 'hkHSTECH', name: '恒生科技指数' }
      ]
    }
  },
  watch: {
    active(value) { if (value) this.$nextTick(this.activate); else this.stopPolling() },
    '$store.state.quoteColorMode'() { applyChartTheme(this.chart, this.$store.state.theme) },
    '$store.state.theme'(theme) { applyChartTheme(this.chart, theme) }
  },
  mounted() {
    window.addEventListener('resize', this.resize)
    this.$nextTick(this.activate)
  },
  beforeDestroy() {
    this.disposed = true
    this.stopPolling()
    window.removeEventListener('resize', this.resize)
    if (this.chart) this.chart.ChartDestroy()
  },
  methods: {
    stopPolling() { if (this.stopRefresh) this.stopRefresh(); this.stopRefresh = null },
    activate() {
      if (this.disposed || !this.active || this.stopRefresh) return
      if (this.chart) { this.resize(); this.requestMinute() }
      else this.resetChart()
      this.stopRefresh = pollMarket(() => this.requestMinute(), MARKET_REFRESH_MS, 'hk')
    },
    requestMinute() {
      if (!this.disposed && this.active && this.chart) return this.chart.JSChartContainer.RequestMinuteData()
    },
    select(index) {
      if (this.selectedIndex === index) return
      this.selectedIndex = index
      this.resetChart()
    },
    resetChart() {
      if (!this.$refs.chart) return
      if (this.chart) this.chart.ChartDestroy()
      this.$refs.chart.innerHTML = ''
      const item = this.indexTabs[this.selectedIndex]
      this.error = ''
      this.option = DefaultData.GetMinuteOption()
      // HQChart uses the .HK suffix to select Hong Kong trading hours.
      this.option.Symbol = `${item.code.substring(2)}.HK`
      this.option.symbol = this.option.Symbol
      this.option.AutoUpdateFrequency = MARKET_REFRESH_MS
      this.option.IsAutoUpdate = false
      this.minuteLoaded = false
      this.option.NetworkFilter = this.networkFilter
      applyChartTheme(null, this.$store.state.theme)
      this.setChartSize()
      this.chart = HQChart.Chart.JSChart.Init(this.$refs.chart)
      this.chart.SetOption(this.option)
      applyChartPricePrecision(this.chart)
      this.resize()
    },
    resize() {
      this.setChartSize()
      if (this.chart) this.chart.OnSize()
    },
    setChartSize() {
      const element = this.$refs.chart
      if (element) {
        element.style.width = `${Math.max(element.parentElement.clientWidth - 25, 1)}px`
        element.style.height = `${Math.floor(window.innerHeight * 0.34495)}px`
      }
    },
    networkFilter(data, callback) {
      if (data.Name !== 'MinuteChartContainer::RequestMinuteData') return
      data.PreventDefault = true
      if (this.disposed || !this.active || document.hidden || this.minuteLoading) return
      this.minuteLoading = true
      const item = this.indexTabs[this.selectedIndex]
      const symbol = item.code
      const chartSymbol = `${symbol.substring(2)}.HK`
      return marketRequest('minute', { code: symbol }, !this.minuteLoaded).then(response => {
        const payload = publicBody(response)
        const stock = payload && payload.data && payload.data[symbol]
        if (this.disposed || !this.active || data.Self.IsDestroy) return
        if (!stock || !stock.data || !stock.data.data.length) throw new Error('暂无分时数据')
        this.minuteLoaded = true
        this.error = ''
        const minute = stock.data
        const quote = stock.qt && stock.qt[symbol]
        const yclose = Number(quote && quote[4]) || 0
        let previousVolume = 0
        let totalAmount = 0
        const points = (minute.data || []).map(row => {
          const [time, price, volume] = row.split(' ')
          const numericPrice = Number(price)
          const cumulativeVolume = Number(volume)
          const minuteVolume = cumulativeVolume - previousVolume
          totalAmount += minuteVolume * numericPrice
          previousVolume = cumulativeVolume
          return {
            time: Number(time),
            price: numericPrice,
            open: numericPrice,
            high: numericPrice,
            low: numericPrice,
            vol: minuteVolume,
            avprice: cumulativeVolume ? totalAmount / cumulativeVolume : numericPrice
          }
        })
        callback({ code: 0, stock: [{ name: item.name, symbol: chartSymbol, date: Number(minute.date), yclose, minute: points }] })
      }).catch(() => {
        if (data.Self.IsDestroy) return
        this.error = '分时数据暂时无法加载'
        callback({ code: 0, stock: [] })
      }).finally(() => { this.minuteLoading = false })
    },
    parseTencentResponse(response, variableName) {
      if (typeof response !== 'string') return response
      const source = response.trim().replace(new RegExp(`^(?:var\\s+)?${variableName}\\s*=`), '').replace(/;$/, '')
      return JSON.parse(source)
    }
  }
}
</script>

<style scoped>
.hong-kong-minute-chart { position: relative; width: 100%; }
.index-tabs { height: 36px; margin-bottom: 6px; background: var(--theme-header); display: flex; }
.index-tabs button { width: 120px; min-width: 0; padding: 0 4px; border: 0; background: transparent; color: var(--theme-label); cursor: pointer; }
.index-tabs button.active { color: var(--theme-text); border-bottom: 2px solid var(--theme-text); }
.chart { height: 34.495vh; background: var(--theme-bg); }
.empty { position: absolute; inset: 80px 0 auto; color: #9da6b3; text-align: center; }
</style>
